/**
 * Create Workspace Modal component
 * Dialog for creating a new workspace
 */

import * as Dialog from '@radix-ui/react-dialog';
import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button';
import Input from '../Input';

interface CreateWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (workspaceName: string) => Promise<void>;
}

export default function CreateWorkspaceModal({ open, onClose, onCreate }: CreateWorkspaceModalProps) {
  const { t } = useTranslation();
  const [workspaceName, setWorkspaceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!workspaceName.trim()) {
      setError(t('userMenu.createWorkspace.errors.nameRequired', 'Workspace name is required'));
      return;
    }

    if (workspaceName.length < 2) {
      setError(t('userMenu.createWorkspace.errors.nameTooShort', 'Workspace name must be at least 2 characters'));
      return;
    }

    setIsCreating(true);

    try {
      await onCreate(workspaceName.trim());
      setWorkspaceName('');
      onClose();
    } catch (err: any) {
      setError(err.message || t('userMenu.createWorkspace.errors.failed', 'Failed to create workspace'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setWorkspaceName('');
      setError('');
      onClose();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 animate-in fade-in-0" />

        <Dialog.Content
          className="
            fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
            w-full max-w-md
            bg-white dark:bg-gray-900
            border-2 border-black dark:border-white
            rounded-lg shadow-brutal
            p-6
            z-50
            animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2
          "
        >
          <Dialog.Title className="text-2xl font-bold mb-2 dark:text-white">
            {t('userMenu.createWorkspace.title', 'Create new workspace')}
          </Dialog.Title>

          <Dialog.Description className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {t('userMenu.createWorkspace.description', 'Create a new workspace to organize your content and team')}
          </Dialog.Description>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label={t('userMenu.createWorkspace.nameLabel', 'Workspace name')}
              type="text"
              value={workspaceName}
              onChange={(e) => setWorkspaceName(e.target.value)}
              placeholder={t('userMenu.createWorkspace.namePlaceholder', 'Acme Inc.')}
              disabled={isCreating}
              autoFocus
              required
            />

            {error && (
              <div className="text-sm text-red-600 dark:text-red-400 p-3 border-2 border-red-600 dark:border-red-400 rounded-lg bg-red-50 dark:bg-red-900/20">
                {error}
              </div>
            )}

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isCreating}
              >
                {t('common.cancel', 'Cancel')}
              </Button>

              <Button
                type="submit"
                variant="primary"
                disabled={isCreating}
              >
                {isCreating
                  ? t('userMenu.createWorkspace.creating', 'Creating...')
                  : t('userMenu.createWorkspace.create', 'Create workspace')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
